// shared/bdTracker.ts
/** The BD tracker payload (GET /api/cois/bd), shared by the server and the COI page. */
import { BD_PERIODS, isBdPeriod, type BdPeriod } from "./bdPeriod";

export const BD_WEEKS = 12;
export const BD_LOG_LIMIT = 200;

export interface BdPerson { id: string; name: string; title: string | null }

export interface BdCounts {
  touchpoints: number; meetings: number; meals: number; calls: number; emails: number;
  coisAdded: number; sourced: number; referralsIn: number; referralsSent: number;
}

export interface BdTiles extends BdCounts {
  sourcedByChannel: { sourceType: string; n: number }[];
  /** COIs the selected person owns; null for Everyone. */
  ownedCois: number | null;
  totalCois: number;
  referralsInYtd: number;
  referralsInYtdMandates: number;
  referralsSentYtd: number;
}

export interface BdScoreRow extends BdCounts { person: BdPerson }

export interface BdWeekly { weeks: string[]; meeting: number[]; call: number[]; email: number[]; meal: number[] }

export interface BdReferredCompany {
  id: string; name: string; category: string; stage: string | null; status: string | null; introducedOn: string;
}

export interface BdReferrer {
  contactId: string | null; name: string; firmName: string | null; specialty: string | null; sourceType: string;
  companies: BdReferredCompany[];
}

export type BdLogKind = "meeting" | "call" | "email" | "coi_added" | "sourced" | "referral_in" | "referral_sent";

export interface BdLogItem {
  key: string;
  at: string;
  kind: BdLogKind;
  meal: boolean;
  title: string;
  /** "Kendall King · Castle Point Wealth", or the source channel. The client appends the specialty label. */
  sub: string | null;
  specialty: string | null;
  by: string[];
  href: string | null;
}

export interface BdPayload {
  period: { period: BdPeriod; label: string; start: string };
  /** "all" or a user id. */
  who: string;
  people: BdPerson[];
  tiles: BdTiles;
  scoreboard: BdScoreRow[];
  weekly: BdWeekly;
  referrers: BdReferrer[];
  touchesByCoi: Record<string, number>;
  log: BdLogItem[];
}

export function companyHref(category: string, id: string): string {
  if (category === "Client") return `/clients/${id}`;
  if (category === "Prospects") return `/prospects/${id}`;
  return `/companies/${id}`;
}

export type BdQuery = { ok: true; period: BdPeriod; who: string } | { ok: false; error: string };

/** `peopleIds` are the active team members; a stale or mistyped `who` is a 400, and the page falls back to Everyone. */
export function parseBdQuery(q: { period?: unknown; who?: unknown }, peopleIds: ReadonlySet<string>): BdQuery {
  const period = q.period === undefined || q.period === "" ? "month" : q.period;
  if (!isBdPeriod(period)) return { ok: false, error: `period must be one of: ${BD_PERIODS.join(", ")}` };
  const who = q.who === undefined || q.who === "" ? "all" : q.who;
  if (who !== "all" && (typeof who !== "string" || !peopleIds.has(who))) {
    return { ok: false, error: "who must be all or an active team member" };
  }
  return { ok: true, period, who: who as string };
}
